import React, { createContext, useContext, useEffect, useState } from 'react';
import { User, Session } from '@supabase/supabase-js';
import { supabase } from '@/lib/supabase';
import { CryptoService } from '@/lib/crypto';

interface AuthContextType {
  user: User | null;
  session: Session | null;
  loading: boolean;
  signIn: (email: string, password: string) => Promise<{ error: Error | null }>;
  signUp: (email: string, password: string) => Promise<{ error: Error | null }>;
  signOut: () => Promise<void>;
  changePassword: (currentPassword: string, newPassword: string) => Promise<{ error: Error | null }>;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

const CREDENTIALS_KEY = 'encrypted_notes_credentials';

export function AuthProvider({ children }: { children: React.ReactNode }) {
  const [user, setUser] = useState<User | null>(null);
  const [session, setSession] = useState<Session | null>(null);
  const [loading, setLoading] = useState(true);

  async function loadMasterKey(email: string, userId: string): Promise<boolean> {
    try {
      const credentials = localStorage.getItem(CREDENTIALS_KEY);
      if (!credentials) {
        await supabase.auth.signOut();
        return false;
      }

      const { password } = JSON.parse(credentials);

      const { data, error } = await supabase
        .from('user_encryption_keys')
        .select('encrypted_master_key')
        .eq('user_id', userId)
        .maybeSingle();

      if (error) {
        return false;
      }

      if (!data) {
        return false;
      }

      const masterKey = CryptoService.decryptMasterKey(
        data.encrypted_master_key,
        password,
        email
      );

      if (masterKey) {
        CryptoService.setMasterKey(masterKey, email, password);
        return true;
      } else {
        return false;
      }
    } catch (error) {
      return false;
    }
  }

  useEffect(() => {
    initializeAuth();

    const { data: { subscription } } = supabase.auth.onAuthStateChange(
      (_event, session) => {
        (async () => {
          setSession(session);
          setUser(session?.user ?? null);

          if (session?.user) {
            await loadMasterKey(session.user.email!, session.user.id);
          } else {
            CryptoService.clearEncryptionKey();
          }
        })();
      }
    );

    return () => {
      subscription.unsubscribe();
    };
  }, []);

  async function initializeAuth() {
    try {
      const { data: { session } } = await supabase.auth.getSession();

      if (session?.user) {
        const success = await loadMasterKey(session.user.email!, session.user.id);

        if (!success) {
          await supabase.auth.signOut();
          setSession(null);
          setUser(null);
          CryptoService.clearEncryptionKey();
        } else {
          setSession(session);
          setUser(session.user);
        }
      } else {
        setSession(null);
        setUser(null);
      }
    } catch (error) {
      setSession(null);
      setUser(null);
    } finally {
      setLoading(false);
    }
  }

  async function signIn(email: string, password: string) {
    try {
      const { error, data } = await supabase.auth.signInWithPassword({
        email,
        password,
      });

      if (error) {
        if (error.message === 'Invalid login credentials') {
          return { error: new Error('Email ou senha incorretos') };
        }
        return { error };
      }

      if (!data.session) {
        return { error: new Error('Sessão não criada') };
      }

      await supabase.auth.setSession({
        access_token: data.session.access_token,
        refresh_token: data.session.refresh_token,
      });

      await new Promise(resolve => setTimeout(resolve, 500));

      const { data: keyData } = await supabase
        .from('user_encryption_keys')
        .select('encrypted_master_key')
        .eq('user_id', data.user.id)
        .maybeSingle();

      let masterKey: string | null = null;

      if (!keyData) {
        masterKey = CryptoService.generateMasterKey();
        const encryptedMasterKey = CryptoService.encryptMasterKey(masterKey, password, email);

        const { error: insertError } = await supabase.rpc('insert_user_encryption_key', {
          p_user_id: data.user.id,
          p_encrypted_master_key: encryptedMasterKey,
        });

        if (insertError) {
          await supabase.auth.signOut();
          return { error: new Error('Erro: ' + insertError.message) };
        }
      } else {
        masterKey = CryptoService.decryptMasterKey(
          keyData.encrypted_master_key,
          password,
          email
        );

        if (!masterKey) {
          await supabase.auth.signOut();
          return { error: new Error('Senha incorreta') };
        }
      }

      localStorage.setItem(
        CREDENTIALS_KEY,
        JSON.stringify({ email, password })
      );

      CryptoService.setMasterKey(masterKey, email, password);

      setSession(data.session);
      setUser(data.user);

      return { error: null };
    } catch (error) {
      return { error: error as Error };
    }
  }

  async function signUp(email: string, password: string) {
    try {
      const { error, data } = await supabase.auth.signUp({
        email,
        password,
      });

      if (error) return { error };
      if (!data.user) return { error: new Error('User creation failed') };

      const masterKey = CryptoService.generateMasterKey();
      const encryptedMasterKey = CryptoService.encryptMasterKey(masterKey, password, email);

      const { error: keyError } = await supabase.rpc('insert_user_encryption_key', {
        p_user_id: data.user.id,
        p_encrypted_master_key: encryptedMasterKey,
      });

      if (keyError) {
        await supabase.auth.signOut();
        return { error: new Error('Failed to create encryption key') };
      }

      localStorage.setItem(
        CREDENTIALS_KEY,
        JSON.stringify({ email, password })
      );

      CryptoService.setMasterKey(masterKey, email, password);

      const { data: { session } } = await supabase.auth.getSession();
      if (session) {
        setSession(session);
        setUser(session.user);
      }

      return { error: null };
    } catch (error) {
      return { error: error as Error };
    }
  }

  async function changePassword(currentPassword: string, newPassword: string) {
    try {
      if (!user?.email) {
        return { error: new Error('User not authenticated') };
      }

      const { data: keyData, error: keyError } = await supabase
        .from('user_encryption_keys')
        .select('encrypted_master_key')
        .eq('user_id', user.id)
        .maybeSingle();

      if (keyError || !keyData) {
        return { error: new Error('Master key not found') };
      }

      const masterKey = CryptoService.decryptMasterKey(
        keyData.encrypted_master_key,
        currentPassword,
        user.email
      );

      if (!masterKey) {
        return { error: new Error('Current password is incorrect') };
      }

      const { error: authError } = await supabase.auth.updateUser({
        password: newPassword,
      });

      if (authError) return { error: authError };

      const newEncryptedMasterKey = CryptoService.encryptMasterKey(
        masterKey,
        newPassword,
        user.email
      );

      const { error: updateError } = await supabase
        .from('user_encryption_keys')
        .update({
          encrypted_master_key: newEncryptedMasterKey,
          updated_at: new Date().toISOString()
        })
        .eq('user_id', user.id);

      if (updateError) return { error: updateError };

      localStorage.setItem(
        CREDENTIALS_KEY,
        JSON.stringify({ email: user.email, password: newPassword })
      );

      CryptoService.setMasterKey(masterKey, user.email, newPassword);

      return { error: null };
    } catch (error) {
      return { error: error as Error };
    }
  }

  async function signOut() {
    localStorage.removeItem(CREDENTIALS_KEY);
    CryptoService.clearEncryptionKey();
    await supabase.auth.signOut();
  }

  return (
    <AuthContext.Provider value={{ user, session, loading, signIn, signUp, signOut, changePassword }}>
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
}
